import { useEffect, useState } from 'react';
import { Button, Col, Drawer, Form, Input, Row, Select } from 'antd';
import { Formik } from 'formik';
import { FiCheck } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';

import { DebounceSelect } from '../../../../components/debonce-select';
import { searchGroups } from '../../TargetGroups.services.ts';
import type { GroupType, TargetGroupDrawerProps } from '../../TargetGroups.types.ts';

import {
  TARGET_GROUPS_FORM,
  TARGET_GROUPS_FORM_SCHEMA,
  TARGET_GROUPS_INITIAL_FORM_VALUES,
} from '../../TargetGroups.constants.tsx';

import './TargetGroupDrawer.styles.css';
import { useTranslation } from 'react-i18next';
import { getTargetGroupsValues } from '../../TargetGroups.helpers.ts';
import { useService } from '../../TargetGroups.hooks.ts';

export default function TargetGroupDrawer({
  onClose,
  open,
  onSubmit,
  targetGroupId,
}: TargetGroupDrawerProps) {
  const [value, setValue] = useState<GroupType[]>([]);
  const { t } = useTranslation();
  const { group } = useService({ targetGroupId });

  const initialValues = targetGroupId
    ? getTargetGroupsValues(group)
    : TARGET_GROUPS_INITIAL_FORM_VALUES;

  useEffect(() => {
    setValue(group?.entraGroupName ? [{ value: group?.id, label: group?.entraGroupName }] : []);
  }, [group?.entraGroupName, group?.id]);
  return (
    <Drawer
      title={targetGroupId ? group?.name : t('pages.target-groups.create')}
      placement="right"
      closable={false}
      width={500}
      onClose={onClose}
      open={open}
      extra={
        <Row justify="end" align="middle" gutter={8}>
          <span onClick={onClose} style={{ cursor: 'pointer' }}>
            <MdClose fontSize={20} />
          </span>
        </Row>
      }
      footer={
        <Row justify="end" gutter={8}>
          <Col>
            <Button size="large" onClick={onClose}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              size="large"
              type="primary"
              icon={<FiCheck />}
              iconPosition="end"
              htmlType="submit"
              form={TARGET_GROUPS_FORM}
            >
              Confirm
            </Button>
          </Col>
        </Row>
      }
    >
      <Formik
        initialValues={initialValues}
        validationSchema={TARGET_GROUPS_FORM_SCHEMA}
        onSubmit={(values: {
          id: string;
          name: string;
          entraGroup: { value: string; label: string }[] | null;
          active: boolean;
        }) => {
          onSubmit(values, value);
        }}
        enableReinitialize
      >
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
          return (
            <Form layout="vertical" onFinish={handleSubmit} id={TARGET_GROUPS_FORM}>
              <Form.Item
                label={t('form.field.name')}
                validateStatus={touched.name && errors.name ? 'error' : ''}
                help={touched.name && typeof errors.name === 'string' ? errors.name : undefined}
              >
                <Input
                  name="name"
                  variant="underlined"
                  value={values.name}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>
              <Form.Item
                label={t('form.field.entra-group')}
                validateStatus={touched.entraGroup && errors.entraGroup ? 'error' : ''}
                help={
                  touched.entraGroup && typeof errors.entraGroup === 'string'
                    ? errors.entraGroup
                    : undefined
                }
              >
                <DebounceSelect
                  mode="multiple"
                  value={values.entraGroup}
                  variant="underlined"
                  placeholder={t('form.placeholder.select-here')}
                  fetchOptions={(term) => searchGroups(term)}
                  style={{ width: '100%' }}
                  maxTagCount={1}
                  onChange={(newValue) => {
                    if (Array.isArray(newValue)) {
                      setFieldValue('entraGroup', newValue, false);
                      setValue(newValue);
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t('form.field.is-active')}
                validateStatus={touched.active && errors.active ? 'error' : ''}
                help={
                  touched.active && typeof errors.active === 'string' ? errors.active : undefined
                }
              >
                <Select
                  variant="underlined"
                  value={values.active}
                  placeholder={t('form.placeholder.select-here')}
                  onChange={(e) => setFieldValue('active', e)}
                  options={[
                    { value: true, label: t('common.yes') },
                    { value: false, label: t('common.no') },
                  ]}
                />
              </Form.Item>
            </Form>
          );
        }}
      </Formik>
      <Col></Col>
    </Drawer>
  );
}
